/**
 * HR's confirmation — the moment money is reserved.
 *
 * Everything on this dialog comes from one call (`GET /proposals/:id/preview`): the live pool,
 * the ceiling, whatever is still pending, and the roster's freshness. One call because three
 * calls can disagree with each other, and the disagreement would be invisible.
 *
 * `max_approvable_cents` is read for the ceiling rather than `pool_cents`, even though they are
 * the same number today. The API states them separately on purpose; a UI that treats them as
 * one is relying on a coincidence.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ErrorNote, IssueList, Money, RosterAge } from '@/components/bits';
import { Alert, Button, Dialog, Field, Input, Textarea } from '@/components/ui';
import { formatCentsPlain, parseAmountToCents } from '@/lib/money';

export function ConfirmDialog({
  open,
  proposalId,
  onClose,
}: {
  open: boolean;
  proposalId: number;
  onClose: () => void;
}) {
  const cache = useQueryClient();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  const preview = useQuery({
    queryKey: ['proposal-preview', proposalId],
    queryFn: () => api.proposalPreview(proposalId),
    enabled: open,
  });

  /** Default to what the manager asked for, capped at the pool — never above the ceiling. */
  useEffect(() => {
    if (!preview.data) return;
    const ceiling = preview.data.max_approvable_cents;
    const requested = preview.data.requested_amount_cents ?? ceiling;
    setAmount(formatCentsPlain(Math.min(requested, ceiling)));
  }, [preview.data]);

  const cents = parseAmountToCents(amount);
  const ceiling = preview.data?.max_approvable_cents ?? 0;
  const overCeiling = cents !== null && cents > ceiling;
  const blocked = (preview.data?.pending_exceptions ?? 0) > 0;

  const confirm = useMutation({
    mutationFn: () =>
      api.confirmProposal(proposalId, {
        approved_amount_cents: cents as number,
        ...(note.trim() ? { note: note.trim() } : {}),
        expected_version: preview.data?.version,
      }),
    onSuccess: async () => {
      onClose();
      await cache.invalidateQueries({ queryKey: ['proposal', proposalId] });
      await cache.invalidateQueries({ queryKey: ['proposals'] });
      await cache.invalidateQueries({ queryKey: ['budget-departments'] });
      await cache.invalidateQueries({ queryKey: ['calculate-roster'] });
      await cache.invalidateQueries({ queryKey: ['calculate-selection'] });
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Confirm the budget"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            busy={confirm.isPending}
            disabled={cents === null || cents <= 0 || overCeiling || blocked}
            onClick={() => confirm.mutate()}
          >
            Reserve this amount
          </Button>
        </>
      }
    >
      {preview.isPending ? (
        <p className="text-muted-foreground">Working out the live figures…</p>
      ) : preview.error ? (
        <ErrorNote error={preview.error} />
      ) : preview.data ? (
        <>
          <RosterAge freshness={preview.data.roster_freshness} />

          <dl className="grid grid-cols-2 gap-y-2">
            <dt className="text-muted-foreground">Pool right now</dt>
            <dd className="text-right">
              <Money cents={preview.data.pool_cents} />
            </dd>
            <dt className="text-muted-foreground">Manager requested</dt>
            <dd className="text-right">
              <Money cents={preview.data.requested_amount_cents} muted />
            </dd>
            <dt className="text-muted-foreground">Most you may approve</dt>
            <dd className="text-right font-medium">
              <Money cents={preview.data.max_approvable_cents} />
            </dd>
          </dl>

          {blocked ? (
            <Alert tone="warn" title="An exception is still pending">
              Decide it before confirming. This is refused in the service and by a database trigger,
              so there is no screen that can go round it.
            </Alert>
          ) : null}

          <IssueList issues={preview.data.issues} />

          <Field
            label="Approve"
            required
            error={
              cents === null && amount.trim() !== ''
                ? 'Enter an amount like 1000 or 1000.50.'
                : overCeiling
                  ? `More than the pool. The most you may approve is ${formatCentsPlain(ceiling)}.`
                  : null
            }
            hint="A reservation, not a ceiling on the claim (D-43)."
          >
            {(props) => (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">S$</span>
                <Input
                  {...props}
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                />
              </div>
            )}
          </Field>

          <Field label="Note (optional)">
            {(props) => (
              <Textarea {...props} value={note} onChange={(event) => setNote(event.target.value)} />
            )}
          </Field>

          {confirm.error ? <ErrorNote error={confirm.error} /> : null}
        </>
      ) : null}
    </Dialog>
  );
}
